// Lista de notas: filtros, agrupamento por coluna e ações (DANFE, XML, cancelar,
// carta de correção, buscar protocolo, corrigir e reenviar).
import React, { useCallback, useEffect, useState } from "react";
import { abrirArquivo, fiscal } from "../api";
import { Aviso, Botao, Campo, Entrada, Modal, Selecao, StatusNota, Texto } from "../components/ui";
import { brl, dataBR, mascaraDocumento, TIPO_NOTA } from "../utils";

const FILTRO_INICIAL = {
  modelo: "", status: "", numero: "", serie: "", destinatario: "",
  emissao_de: "", emissao_ate: "", saida_de: "", saida_ate: "", agrupar_por: "",
};

const AGRUPAR = [["status", "Status"], ["modelo", "Tipo"], ["serie", "Série"], ["destinatario", "Destinatário"],
  ["uf", "UF"], ["natureza_operacao", "Natureza"], ["data_emissao", "Data de emissão"]];

export default function NotasLista({ onCorrigir, onSubstituir }) {
  const [filtro, setFiltro] = useState(FILTRO_INICIAL);
  const [dados, setDados] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [acao, setAcao] = useState(null); // {tipo, nota}
  const set = (c, v) => setFiltro((f) => ({ ...f, [c]: v }));

  const params = { ...filtro, status: filtro.status ? [filtro.status] : undefined, pagina, por_pagina: 50 };

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setDados(await fiscal.notas(params));
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const colunas = (n) => (
    <tr key={n.id} className="hover:bg-gray-50">
      <td className="px-3 py-2">{TIPO_NOTA[n.modelo] || n.modelo}</td>
      <td className="px-3 py-2 tabular-nums">{n.numero ?? "—"}</td>
      <td className="px-3 py-2">{n.serie}</td>
      <td className="px-3 py-2">{dataBR(n.data_emissao, true)}</td>
      <td className="px-3 py-2">{n.modelo === "55" ? dataBR(n.data_saida) : "—"}</td>
      <td className="px-3 py-2">
        <div>{n.destinatario?.nome || "Consumidor"}</div>
        <div className="text-xs text-gray-500">{n.destinatario?.documento ? mascaraDocumento(n.destinatario.documento) : ""}</div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{brl(n.totais?.valor_nota)}</td>
      <td className="px-3 py-2"><StatusNota status={n.status} /></td>
      <td className="px-3 py-2 text-right">
        <Acoes nota={n} onAcao={(tipo) => setAcao({ tipo, nota: n })} onCorrigir={onCorrigir} onSubstituir={onSubstituir} recarregar={carregar} />
      </td>
    </tr>
  );

  const cabecalho = (
    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
      <tr>
        <th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Número</th><th className="px-3 py-2">Série</th>
        <th className="px-3 py-2">Emissão</th><th className="px-3 py-2">Saída</th><th className="px-3 py-2">Destinatário</th>
        <th className="px-3 py-2 text-right">Valor</th><th className="px-3 py-2">Status</th><th />
      </tr>
    </thead>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Notas fiscais</h1>
        <Botao variante="secundario" onClick={() => abrirArquivo("/exportacao/notas.csv", { params, nome: "notas.csv", baixar: true })}>
          Exportar CSV
        </Botao>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-lg border bg-white p-4 md:grid-cols-6 border-gray-200">
        <Campo rotulo="Tipo"><Selecao value={filtro.modelo} vazio="Todos" onChange={(e) => set("modelo", e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"], ["nfse", "NFS-e"]]} /></Campo>
        <Campo rotulo="Status">
          <Selecao value={filtro.status} vazio="Todos" onChange={(e) => set("status", e.target.value)}
            opcoes={[["autorizada", "Autorizada"], ["processando", "Processando"], ["rejeitada", "Rejeitada"], ["erro", "Erro"], ["cancelada", "Cancelada"], ["substituida", "Substituída"], ["denegada", "Denegada"], ["inutilizada", "Inutilizada"]]} />
        </Campo>
        <Campo rotulo="Número NF-e"><Entrada type="number" value={filtro.numero} onChange={(e) => set("numero", e.target.value)} /></Campo>
        <Campo rotulo="Série"><Entrada type="number" value={filtro.serie} onChange={(e) => set("serie", e.target.value)} /></Campo>
        <Campo rotulo="Destinatário" className="col-span-2"><Entrada value={filtro.destinatario} placeholder="Nome ou CPF/CNPJ" onChange={(e) => set("destinatario", e.target.value)} /></Campo>
        <Campo rotulo="Emissão de"><Entrada type="date" value={filtro.emissao_de} onChange={(e) => set("emissao_de", e.target.value)} /></Campo>
        <Campo rotulo="Emissão até"><Entrada type="date" value={filtro.emissao_ate} onChange={(e) => set("emissao_ate", e.target.value)} /></Campo>
        <Campo rotulo="Saída de"><Entrada type="date" value={filtro.saida_de} onChange={(e) => set("saida_de", e.target.value)} /></Campo>
        <Campo rotulo="Saída até"><Entrada type="date" value={filtro.saida_ate} onChange={(e) => set("saida_ate", e.target.value)} /></Campo>
        <Campo rotulo="Agrupar por"><Selecao value={filtro.agrupar_por} vazio="Sem agrupamento" onChange={(e) => set("agrupar_por", e.target.value)} opcoes={AGRUPAR} /></Campo>
        <div className="flex items-end"><Botao variante="secundario" onClick={() => { setFiltro(FILTRO_INICIAL); setPagina(1); }}>Limpar</Botao></div>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {carregando && !dados && <p className="text-sm text-gray-500">Carregando…</p>}

      {dados?.grupos && (
        <div className="space-y-3">
          {dados.grupos.map((g) => (
            <details key={String(g.chave)} open className="rounded-lg border bg-white border-gray-200">
              <summary className="flex cursor-pointer justify-between px-4 py-3 text-sm font-semibold">
                <span>{rotuloGrupo(filtro.agrupar_por, g.chave)} · {g.quantidade} nota(s)</span>
                <span className="tabular-nums">{brl(g.valor_total)}</span>
              </summary>
              <div className="overflow-x-auto border-t border-gray-200">
                <table className="min-w-full text-sm">{cabecalho}<tbody className="divide-y">{g.notas.map(colunas)}</tbody></table>
              </div>
            </details>
          ))}
        </div>
      )}

      {dados?.notas && (
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200">
          <table className="min-w-full text-sm">
            {cabecalho}
            <tbody className="divide-y">
              {dados.notas.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-gray-500">Nenhuma nota encontrada</td></tr>}
              {dados.notas.map(colunas)}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t px-4 py-2 text-sm border-gray-200">
            <span>{dados.total} nota(s)</span>
            <div className="flex gap-2">
              <Botao variante="secundario" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Botao>
              <Botao variante="secundario" disabled={pagina * 50 >= dados.total} onClick={() => setPagina((p) => p + 1)}>Próxima</Botao>
            </div>
          </div>
        </div>
      )}

      <ModalCancelar acao={acao} onFechar={() => setAcao(null)} onFeito={carregar} />
      <ModalCartaCorrecao acao={acao} onFechar={() => setAcao(null)} />
      <ModalDetalhe acao={acao} onFechar={() => setAcao(null)} />
    </div>
  );
}

function rotuloGrupo(campo, chave) {
  if (campo === "modelo") return TIPO_NOTA[chave] || chave;
  if (campo === "status") return chave.charAt(0).toUpperCase() + chave.slice(1);
  return chave;
}

function Acoes({ nota, onAcao, onCorrigir, onSubstituir, recarregar }) {
  const [aberto, setAberto] = useState(false);
  const [msg, setMsg] = useState(null);
  const autorizada = nota.status === "autorizada";
  const exec = async (fn) => {
    setAberto(false);
    try {
      await fn();
    } catch (e) {
      setMsg(e.message);
      setTimeout(() => setMsg(null), 6000);
    }
  };
  const itens = [
    ["Ver detalhes", true, () => onAcao("detalhe")],
    [nota.modelo === "nfse" ? "PDF da NFS-e" : "DANFE (PDF)", autorizada || ["cancelada", "substituida"].includes(nota.status), () => exec(() => abrirArquivo(`/notas/${nota.id}/pdf`))],
    ["Baixar XML", autorizada || ["cancelada", "substituida"].includes(nota.status), () => exec(() => abrirArquivo(`/notas/${nota.id}/xml`, { baixar: true, nome: `${nota.chave || nota.numero}.xml` }))],
    ["Buscar protocolo", nota.status === "processando", () => exec(async () => { await fiscal.buscarProtocolo(nota.id); recarregar(); })],
    ["Corrigir e reenviar", ["55", "nfse"].includes(nota.modelo) && ["rejeitada", "erro"].includes(nota.status) && onCorrigir, () => onCorrigir(nota)],
    ["Reenviar", nota.modelo === "65" && ["rejeitada", "erro"].includes(nota.status), () => exec(async () => { await fiscal.reenviar(nota.id, "65"); recarregar(); })],
    ["Carta de correção", autorizada && nota.modelo === "55", () => onAcao("cce")],
    ["Substituir NFS-e", autorizada && nota.modelo === "nfse" && onSubstituir, () => onSubstituir(nota)],
    ["Cancelar nota", autorizada, () => onAcao("cancelar")],
  ].filter(([, visivel]) => visivel);

  return (
    <div className="relative inline-block text-left">
      <Botao variante="fantasma" onClick={() => setAberto((a) => !a)}>Ações ▾</Botao>
      {aberto && (
        <div className="absolute right-0 z-20 mt-1 w-52 rounded-md border bg-white py-1 shadow-lg border-gray-200" onMouseLeave={() => setAberto(false)}>
          {itens.map(([rotulo, , fn]) => (
            <button key={rotulo} type="button" onClick={fn}
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${rotulo === "Cancelar nota" ? "text-red-600" : ""}`}>
              {rotulo}
            </button>
          ))}
        </div>
      )}
      {msg && <div className="absolute right-0 z-20 mt-1 w-64 rounded bg-red-50 p-2 text-xs text-red-800 shadow">{msg}</div>}
    </div>
  );
}

function ModalCancelar({ acao, onFechar, onFeito }) {
  const [just, setJust] = useState("");
  const [motivo, setMotivo] = useState("1");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const aberto = acao?.tipo === "cancelar";
  useEffect(() => { setJust(""); setErro(null); }, [aberto]);
  if (!aberto) return null;
  const n = acao.nota;
  const enviar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const r = await fiscal.cancelar(n.id, just, motivo);
      if (!r.cancelada) throw new Error(r.motivo || "SEFAZ não homologou o cancelamento");
      onFeito();
      onFechar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal aberto titulo={`Cancelar ${TIPO_NOTA[n.modelo]} nº ${n.numero ?? ""}`} onFechar={onFechar}>
      <div className="space-y-3">
        <Aviso tipo="atencao">
          {n.modelo === "nfse"
            ? "O cancelamento é definitivo. O prazo e as regras são do município; fora do prazo a prefeitura recusa e pode exigir processo administrativo."
            : "O cancelamento é definitivo. Prazo: NF-e até 24 h após a autorização; NFC-e normalmente 30 min (varia por estado)."}
        </Aviso>
        {n.modelo === "nfse" && (
          <Campo rotulo="Motivo">
            <Selecao value={motivo} onChange={(e) => setMotivo(e.target.value)}
              opcoes={[["1", "Erro na emissão"], ["2", "Serviço não prestado"], ["9", "Outros"]]} />
          </Campo>
        )}
        <Campo rotulo="Justificativa" dica={`${just.length}/255 — mínimo 15 caracteres`}>
          <Texto value={just} maxLength={255} onChange={(e) => setJust(e.target.value)} />
        </Campo>
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <div className="flex justify-end gap-2">
          <Botao variante="secundario" onClick={onFechar}>Voltar</Botao>
          <Botao variante="perigo" carregando={enviando} disabled={just.trim().length < 15} onClick={enviar}>Cancelar nota</Botao>
        </div>
      </div>
    </Modal>
  );
}

function ModalCartaCorrecao({ acao, onFechar }) {
  const aberto = acao?.tipo === "cce";
  const [texto, setTexto] = useState("");
  const [historico, setHistorico] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState(null);
  const n = acao?.nota;

  useEffect(() => {
    if (!aberto) return;
    setMsg(null);
    fiscal.cartas(n.id).then((h) => {
      setHistorico(h);
      // cada CC-e substitui a anterior: parte do texto da última registrada
      const ultima = h.find((c) => ["registrado", "homologado"].includes(String(c.status).toLowerCase()));
      setTexto(ultima?.texto || "");
    }).catch(() => setHistorico([]));
  }, [aberto, n]);

  if (!aberto) return null;
  const enviar = async () => {
    setEnviando(true);
    setMsg(null);
    try {
      const r = await fiscal.cartaCorrecao(n.id, texto);
      setMsg({ tipo: "sucesso", texto: `Carta de correção nº ${r.sequencia}: ${r.status}${r.motivo ? ` — ${r.motivo}` : ""}` });
      setHistorico(await fiscal.cartas(n.id));
    } catch (e) {
      setMsg({ tipo: "erro", texto: e.message });
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal aberto titulo={`Carta de correção — NF-e nº ${n.numero}`} onFechar={onFechar} largura="max-w-2xl">
      <div className="space-y-3">
        <Aviso>
          Não é permitido corrigir valores, impostos, CFOP, quantidades, dados que mudem remetente ou destinatário, nem datas.
          A nova carta substitui a anterior: escreva todas as correções.
        </Aviso>
        <Campo rotulo="Correção" dica={`${texto.length}/1000 — mínimo 15 caracteres`}>
          <Texto rows={5} value={texto} maxLength={1000} onChange={(e) => setTexto(e.target.value)} />
        </Campo>
        {msg && <Aviso tipo={msg.tipo}>{msg.texto}</Aviso>}
        <div className="flex justify-end">
          <Botao carregando={enviando} disabled={texto.trim().length < 15} onClick={enviar}>Enviar carta de correção</Botao>
        </div>
        <h4 className="pt-2 text-sm font-semibold">Histórico</h4>
        {historico.length === 0 && <p className="text-sm text-gray-500">Nenhuma carta de correção.</p>}
        <ul className="space-y-2">
          {historico.map((c) => (
            <li key={c.id} className="rounded border p-3 text-sm border-gray-200">
              <div className="flex justify-between text-xs text-gray-500">
                <span>Nº {c.sequencia} · {dataBR(c.criado_em, true)} · {c.status} {c.protocolo ? `· protocolo ${c.protocolo}` : ""}</span>
                <button type="button" className="text-blue-700 hover:underline" onClick={() => abrirArquivo(`/nfe/${n.id}/carta-correcao/pdf`)}>PDF</button>
              </div>
              <p className="mt-1 whitespace-pre-line">{c.texto}</p>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

function ModalDetalhe({ acao, onFechar }) {
  const aberto = acao?.tipo === "detalhe";
  const [nota, setNota] = useState(null);
  useEffect(() => {
    if (aberto) fiscal.nota(acao.nota.id).then(setNota).catch(() => setNota(null));
    else setNota(null);
  }, [aberto, acao]);
  if (!aberto) return null;
  const n = nota || acao.nota;
  return (
    <Modal aberto titulo={`${TIPO_NOTA[n.modelo]} nº ${n.numero ?? "—"} — série ${n.serie}`} onFechar={onFechar} largura="max-w-3xl">
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div><span className="text-gray-500">Status:</span> <StatusNota status={n.status} /></div>
          <div><span className="text-gray-500">Protocolo:</span> {n.protocolo || "—"}</div>
          <div className="col-span-2 break-all"><span className="text-gray-500">Chave:</span> {n.chave || "—"}</div>
          {n.motivo_status && <div className="col-span-2"><span className="text-gray-500">{n.modelo === "nfse" ? "Retorno da prefeitura:" : "Retorno SEFAZ:"}</span> {n.codigo_status ? `[${n.codigo_status}] ` : ""}{n.motivo_status}</div>}
          <div><span className="text-gray-500">Destinatário:</span> {n.destinatario?.nome || "Consumidor"}{n.destinatario?.documento ? ` · ${n.destinatario.documento.length === 14 ? "CNPJ" : "CPF"} ${mascaraDocumento(n.destinatario.documento)}` : n.modelo === "65" ? " (sem CPF na nota)" : ""}</div>
          <div><span className="text-gray-500">Valor:</span> {brl(n.totais?.valor_nota)}</div>
          {n.modelo === "nfse" && (
            <>
              <div><span className="text-gray-500">Código de verificação:</span> {n.codigo_verificacao || "—"}</div>
              <div><span className="text-gray-500">DPS:</span> {n.dps?.serie ? `série ${n.dps.serie} nº ${n.dps.numero}` : "—"}</div>
              <div><span className="text-gray-500">ISS:</span> {brl(n.totais?.valor_iss)}{n.totais?.iss_retido ? " (retido)" : ""}</div>
              <div><span className="text-gray-500">Valor líquido:</span> {brl(n.totais?.valor_liquido)}</div>
              {nota?.servico && <div className="col-span-2"><span className="text-gray-500">Serviço:</span> {nota.servico.codigo_tributacao_nacional} — {nota.servico.discriminacao}</div>}
              {n.link_consulta && <div className="col-span-2"><a className="text-blue-700 underline" href={n.link_consulta} target="_blank" rel="noreferrer">Consultar na prefeitura</a></div>}
            </>
          )}
        </div>
        {nota?.itens && (
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 text-left text-gray-500"><tr><th className="p-2">Item</th><th className="p-2">NCM</th><th className="p-2">CFOP</th><th className="p-2 text-right">Qtd</th><th className="p-2 text-right">Unit.</th></tr></thead>
            <tbody className="divide-y">
              {nota.itens.map((i, k) => (
                <tr key={k}><td className="p-2">{i.descricao}</td><td className="p-2">{i.ncm}</td><td className="p-2">{i.tributos?.cfop}</td><td className="p-2 text-right">{i.quantidade}</td><td className="p-2 text-right">{brl(i.valor_unitario)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        {nota?.historico && (
          <div>
            <h4 className="mb-1 font-semibold">Histórico</h4>
            <ul className="space-y-1 text-xs">
              {nota.historico.map((h, k) => <li key={k}>{dataBR(h.em, true)} — {h.status}{h.mensagem ? `: ${h.mensagem}` : ""}</li>)}
              {(nota.eventos || []).map((e, k) => <li key={`e${k}`}>{dataBR(e.criado_em, true)} — {e.tipo === "cancelamento" ? "Cancelamento" : `CC-e nº ${e.sequencia}`}: {e.status}</li>)}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
